import { type FC, useEffect, useRef, useState } from 'react';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Calendar } from './calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { CalendarIcon, CheckIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { cn } from '@/utils/utils.ts';
import { Alert, AlertDescription } from '@/components/ui/alert.tsx';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer.tsx';
import { useTranslation } from 'react-i18next';

type RangeGranularity = '1min' | '5min' | 'hourly' | 'daily' | 'monthly';

export interface DateRangePickerProps {
  /** Click handler for applying the updates from DateRangePicker. */
  onUpdate?: (values: { range: DateRange; rangeGranularity: string }) => void;
  /** Initial value for start date */
  initialDateFrom?: Date | string;
  /** Initial value for end date */
  initialDateTo?: Date | string;
  /** Initial value for start date for compare */
  initialCompareFrom?: Date | string;
  /** Initial value for end date for compare */
  initialCompareTo?: Date | string;
  /** Option for locale */
  locale?: string;
  /** Option for granularity */
  defaultGranularity?: RangeGranularity;

  disabledGranularities?: RangeGranularity[];
  hideGranularities?: RangeGranularity[];
  /** Style for button */
  className?: string;
}

const formatDate = (date: Date, locale: string = 'en-GB'): string => {
  return date.toLocaleDateString(locale, {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
};

const getDateAdjustedForTimezone = (dateInput: Date | string): Date => {
  if (typeof dateInput === 'string') {
    // Split the date string to get year, month, and day parts
    const parts = dateInput.split('-').map((part) => parseInt(part, 10));
    // Create a new Date object using the local timezone
    // Note: Month is 0-indexed, so subtract 1 from the month part
    return new Date(parts[0], parts[1] - 1, parts[2]);
  } else {
    // If dateInput is already a Date object, return it directly
    return dateInput;
  }
};

const GRANULARITY_CONSTRAINTS: {
  [key in RangeGranularity]: {
    maxDays?: number;
    errorMessage?: string;
  };
} = {
  '1min': {
    maxDays: 1,
    errorMessage: 'A 1-minute granularity is not available beyond a past 1-day period.',
  },
  '5min': {
    maxDays: 1,
    errorMessage: 'A 5-minute granularity is not available beyond a past 1-day period.',
  },
  hourly: {
    maxDays: 3,
    errorMessage: 'Hourly granularity is not available beyond a past 3-day period.',
  },
  daily: {
    maxDays: 62,
    errorMessage: 'Daily granularity is not available beyond a past 2-month period.',
  },
  monthly: {},
};

const granularityRules: {
  minDays: number;
  maxDays?: number;
  fromPresets: RangeGranularity[];
  toPreset: RangeGranularity;
}[] = [
  {
    minDays: 1,
    maxDays: 1,
    fromPresets: ['1min'],
    toPreset: '1min',
  },
  {
    minDays: 1,
    maxDays: 1,
    fromPresets: ['1min', '5min', 'hourly', 'daily', 'monthly'],
    toPreset: '5min',
  },
  {
    minDays: 1,
    maxDays: 3,
    fromPresets: ['1min', '5min', 'daily', 'monthly'],
    toPreset: 'hourly',
  },
  {
    minDays: 3,
    maxDays: 62,
    fromPresets: ['1min', '5min', 'hourly'],
    toPreset: 'daily',
  },
  {
    minDays: 62,
    fromPresets: ['1min', '5min', 'hourly', 'daily'],
    toPreset: 'monthly',
  },
];

interface DateRange {
  from: Date;
  to: Date | undefined;
}

function calculateDateDifference({ from, to }: DateRange): {
  days: number;
  weeks: number;
  months: number;
  years: number;
} {
  if (!to) {
    to = new Date(from.setHours(23, 59, 59, 999));
  }

  const diffTime = Math.abs(to.getTime() - from.getTime());
  const diffDays = Math.round(((diffTime / (1000 * 60 * 60 * 24)) * 100) / 100);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths =
    (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
  const diffYears = to.getFullYear() - from.getFullYear();
  return { days: diffDays, weeks: diffWeeks, months: diffMonths, years: diffYears };
}

const PRESETS = [
  { name: 'today', label: 'Today' },
  { name: 'yesterday', label: 'Yesterday' },
  { name: 'this_week', label: 'This Week' },
  { name: 'last_week', label: 'Last Week' },
  { name: 'this_month', label: 'This Month' },
  { name: 'last_month', label: 'Last Month' },
] as const;

const GRANULARITY = [
  { name: '1min', label: '1 Minutes' },
  { name: '5min', label: '5 Minutes' },
  { name: 'hourly', label: 'Hourly' },
  { name: 'daily', label: 'Daily' },
  { name: 'monthly', label: 'Monthly' },
] as const;

export const DateRangePicker: FC<DateRangePickerProps> = ({
  initialDateFrom = new Date(new Date().setHours(0, 0, 0, 0)),
  initialDateTo,
  onUpdate,
  locale = 'en-GB',
  defaultGranularity = 'hourly',
  hideGranularities = ['1min'],
  className = '',
}) => {
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'components.date_range_picker',
  });
  const isMobile = useMediaQuery('(max-width:640px)');
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<{
    isError: boolean;
    message: string | undefined;
  }>({ isError: false, message: undefined });

  const [range, setRange] = useState<DateRange>({
    from: getDateAdjustedForTimezone(initialDateFrom),
    to: initialDateTo
      ? getDateAdjustedForTimezone(initialDateTo)
      : getDateAdjustedForTimezone(initialDateFrom),
  });

  const [month, setMonth] = useState(range.from);

  const openedRangeRef = useRef<DateRange | undefined>(undefined);

  const [selectedPreset, setSelectedPreset] = useState<string | undefined>(undefined);
  const [selectedGranularityPreset, setSelectedGranularityPreset] =
    useState<RangeGranularity>(defaultGranularity);

  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 960 : false
  );

  useEffect(() => {
    const handleResize = (): void => {
      setIsSmallScreen(window.innerWidth < 960);
    };

    window.addEventListener('resize', handleResize);

    // Clean up event listener on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getPresetRange = (presetName: string): DateRange => {
    const preset = PRESETS.find(({ name }) => name === presetName);
    if (!preset) throw new Error(`Unknown date range preset: ${presetName}`);
    const from = new Date();
    const to = new Date();

    switch (preset.name) {
      case 'today':
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        break;
      case 'yesterday':
        from.setDate(from.getDate() - 1);
        from.setHours(0, 0, 0, 0);
        to.setDate(to.getDate() - 1);
        to.setHours(23, 59, 59, 999);
        break;
      case 'this_week':
        from.setDate(from.getDate() + 1 - from.getDay());
        from.setHours(0, 0, 0, 0);
        // to.setDate(to.getDate() + (7 - to.getDay()));
        to.setHours(23, 59, 59, 999);
        break;
      case 'last_week':
        from.setDate(from.getDate() - 6 - from.getDay());
        to.setDate(to.getDate() - to.getDay());
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        break;
      case 'this_month':
        from.setDate(1);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        break;
      case 'last_month':
        from.setMonth(from.getMonth() - 1);
        from.setDate(1);
        from.setHours(0, 0, 0, 0);
        to.setDate(0);
        to.setHours(23, 59, 59, 999);
        break;
    }

    return { from, to };
  };

  const setPreset = (preset: string): void => {
    const range = getPresetRange(preset);

    const { days } = calculateDateDifference(range);

    const newGranularity = granularityRules.find(
      (rule) =>
        days >= rule.minDays &&
        (!rule.maxDays || days <= rule?.maxDays) &&
        rule.fromPresets.includes(selectedGranularityPreset)
    );

    if (newGranularity) {
      setSelectedGranularityPreset(newGranularity.toPreset);
    }

    setRange(range);
    setMonth(range.from);
  };

  const checkPreset = (): void => {
    for (const preset of PRESETS) {
      const presetRange = getPresetRange(preset.name);

      const normalizedRangeFrom = new Date(range.from);
      normalizedRangeFrom.setHours(0, 0, 0, 0);
      const normalizedPresetFrom = new Date(presetRange.from.setHours(0, 0, 0, 0));

      const normalizedRangeTo = new Date(range.to ?? 0);
      normalizedRangeTo.setHours(0, 0, 0, 0);
      const normalizedPresetTo = new Date(presetRange.to?.setHours(0, 0, 0, 0) ?? 0);

      if (
        normalizedRangeFrom.getTime() === normalizedPresetFrom.getTime() &&
        normalizedRangeTo.getTime() === normalizedPresetTo.getTime()
      ) {
        setSelectedPreset(preset.name);
        return;
      }
    }

    setSelectedPreset(undefined);
  };

  const resetValues = (): void => {
    setRange({
      from:
        typeof initialDateFrom === 'string'
          ? getDateAdjustedForTimezone(initialDateFrom)
          : initialDateFrom,
      to: initialDateTo
        ? typeof initialDateTo === 'string'
          ? getDateAdjustedForTimezone(initialDateTo)
          : initialDateTo
        : typeof initialDateFrom === 'string'
          ? getDateAdjustedForTimezone(initialDateFrom)
          : initialDateFrom,
    });

    setMonth(
      typeof initialDateFrom === 'string'
        ? getDateAdjustedForTimezone(initialDateFrom)
        : initialDateFrom
    );

    setError({
      isError: false,
      message: undefined,
    });
  };

  useEffect(() => {
    checkPreset();
  }, [range]);

  const PresetButton = ({
    preset,
    label,
    isSelected,
  }: {
    preset: string;
    label: string;
    isSelected: boolean;
  }) => (
    <Button
      className={cn(isSelected && 'pointer-events-none')}
      variant="ghost"
      onClick={() => {
        setPreset(preset);
      }}
    >
      <>
        <span className={cn('pr-2 opacity-0', isSelected && 'opacity-70')}>
          <CheckIcon width={18} height={18} />
        </span>
        {label}
      </>
    </Button>
  );

  const PresetGranularityButton = ({
    preset,
    label,
    isSelected,
  }: {
    preset: RangeGranularity;
    label: string;
    isSelected: boolean;
  }) => (
    <Button
      className={cn(
        isSelected && 'pointer-events-none',
        hideGranularities?.includes(preset) && 'hidden'
      )}
      variant="ghost"
      onClick={() => {
        setSelectedGranularityPreset(preset);
      }}
    >
      <>
        <span className={cn('pr-1 opacity-0', isSelected && 'opacity-70')}>
          <CheckIcon width={18} height={18} />
        </span>
        {label}
      </>
    </Button>
  );

  useEffect(() => {
    if (isOpen) {
      openedRangeRef.current = range;
    }
  }, [isOpen]);

  const formatButtonDate = (from: Date, to: Date | undefined) => {
    if (!from) return <span>Select a date</span>;

    if (!to) return <span>{formatDate(from, locale)}</span>;

    if (formatDate(from, locale) === formatDate(to, locale))
      return <span>{formatDate(from, locale)}</span>;

    return (
      <div>{`${formatDate(from, locale)}${
        to != null ? ' - ' + formatDate(to, locale) : ''
      }`}</div>
    );
  };

  if (isMobile) {
    return (
      <Drawer
        open={isOpen}
        onOpenChange={(open: boolean) => {
          if (!open) {
            resetValues();
          }
          setIsOpen(open);
        }}
      >
        <DrawerTrigger asChild>
          <Button variant="outline" className={cn('gap-2 px-3 font-normal', className)}>
            <CalendarIcon className="size-4" />
            <div className="text-right">
              <div className="py-1">{formatButtonDate(range.from, range.to)}</div>
            </div>
          </Button>
        </DrawerTrigger>
        <DrawerContent className="flex flex-col gap-2 px-4">
          <DrawerHeader className="p-2">
            <DrawerTitle>Select Date Range</DrawerTitle>
          </DrawerHeader>
          <div className="flex gap-2">
            <Select
              defaultValue={selectedPreset}
              onValueChange={(value) => {
                setPreset(value);
              }}
            >
              <SelectTrigger className="mx-auto mb-2">
                <SelectValue placeholder="Select a preset" />
              </SelectTrigger>
              <SelectContent>
                {PRESETS.map((preset) => (
                  <SelectItem key={preset.name} value={preset.name}>
                    {t ? t(`presets.${preset.name}`) : preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={selectedGranularityPreset}
              onValueChange={(value) => {
                setSelectedGranularityPreset(value as RangeGranularity);
              }}
            >
              <SelectTrigger className="mx-auto mb-2">
                <SelectValue placeholder="Select a preset" />
              </SelectTrigger>
              <SelectContent>
                {GRANULARITY.map((preset) => (
                  <SelectItem
                    className={cn(hideGranularities?.includes(preset.name) && 'hidden')}
                    key={preset.name}
                    value={preset.name}
                  >
                    {t ? t(`granularity.${preset.name}`) : preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex min-h-[370px] justify-center">
            <Calendar
              localeDayPicker={i18n.language as 'ro' | 'en'}
              className="p-0"
              classNames={{ weekdays: 'flex gap-1 pl-0.5' }}
              dayButtonClassName="p-5"
              mode="range"
              onSelect={(value: { from?: Date; to?: Date } | undefined) => {
                if (!value?.from) return;

                const range = {
                  from: new Date(value.from.setHours(0, 0, 0, 0)),
                  to: new Date(
                    value.to?.setHours(23, 59, 59, 999) ?? value.from.setHours(23, 59, 59, 999)
                  ),
                };

                const { days } = calculateDateDifference(range);

                const newGranularity = granularityRules.find(
                  (rule) =>
                    days >= rule.minDays &&
                    (!rule.maxDays || days <= rule?.maxDays) &&
                    rule.fromPresets.includes(selectedGranularityPreset)
                );

                if (newGranularity) {
                  setSelectedGranularityPreset(newGranularity.toPreset);
                }

                setRange(range);
              }}
              selected={range}
              numberOfMonths={1}
              month={month}
              onMonthChange={setMonth}
            />
          </div>
          <div className="mt-2 flex gap-4 pb-4">
            <Button
              onClick={() => {
                setIsOpen(false);
                resetValues();
              }}
              variant="ghost"
              className="w-1/3"
            >
              {t ? t('buttons.cancel') : 'Cancel'}
            </Button>
            <Button
              className="w-2/3"
              onClick={() => {
                const { days } = calculateDateDifference(range);
                const constraint = GRANULARITY_CONSTRAINTS[selectedGranularityPreset];

                const errorMessage = t
                  ? t(`constraint_errors.${selectedGranularityPreset}`)
                  : constraint.errorMessage;

                if (constraint && constraint?.maxDays && days > constraint.maxDays) {
                  setError({
                    isError: true,
                    message: errorMessage,
                  });

                  return;
                }

                setError({
                  isError: false,
                  message: undefined,
                });

                setIsOpen(false);
                onUpdate?.({
                  range,
                  rangeGranularity: selectedGranularityPreset as RangeGranularity,
                });
              }}
            >
              {t ? t('buttons.update') : 'Update'}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover
      modal={true}
      open={isOpen}
      onOpenChange={(open: boolean) => {
        if (!open) {
          resetValues();
        }
        setIsOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn('gap-2 px-3 font-normal', className)}>
          <CalendarIcon className="size-4" />
          <div className="text-right">
            <div className="py-1">{formatButtonDate(range.from, range.to)}</div>
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto">
        <div className="flex">
          <div className="flex">
            <div className="flex flex-col">
              {isSmallScreen && (
                <>
                  <Select
                    defaultValue={selectedPreset}
                    onValueChange={(value) => {
                      setPreset(value);
                    }}
                  >
                    <SelectTrigger className="mx-auto mb-2">
                      <SelectValue placeholder="Select a preset" />
                    </SelectTrigger>
                    <SelectContent>
                      {PRESETS.map((preset) => (
                        <SelectItem key={preset.name} value={preset.name}>
                          {t ? t(`presets.${preset.name}`) : preset.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={selectedGranularityPreset}
                    onValueChange={(value) => {
                      setSelectedGranularityPreset(value as RangeGranularity);
                    }}
                  >
                    <SelectTrigger className="mx-auto mb-2">
                      <SelectValue placeholder="Select a preset" />
                    </SelectTrigger>
                    <SelectContent>
                      {GRANULARITY.map((preset) => (
                        <SelectItem
                          className={cn(hideGranularities?.includes(preset.name) && 'hidden')}
                          key={preset.name}
                          value={preset.name}
                        >
                          {t ? t(`granularity.${preset.name}`) : preset.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
              <div>
                <Calendar
                  localeDayPicker={i18n.language as 'ro' | 'en'}
                  mode="range"
                  onSelect={(value: { from?: Date; to?: Date } | undefined) => {
                    if (!value?.from) return;

                    const range = {
                      from: new Date(value.from.setHours(0, 0, 0, 0)),
                      to: new Date(
                        value.to?.setHours(23, 59, 59, 999) ??
                          value.from.setHours(23, 59, 59, 999)
                      ),
                    };

                    const { days } = calculateDateDifference(range);

                    const newGranularity = granularityRules.find(
                      (rule) =>
                        days >= rule.minDays &&
                        (!rule.maxDays || days <= rule?.maxDays) &&
                        rule.fromPresets.includes(selectedGranularityPreset)
                    );

                    if (newGranularity) {
                      setSelectedGranularityPreset(newGranularity.toPreset);
                    }

                    setRange(range);
                  }}
                  selected={range}
                  numberOfMonths={isSmallScreen ? 1 : 2}
                  month={month}
                  onMonthChange={setMonth}
                />
              </div>
            </div>
          </div>
          {!isSmallScreen && (
            <div className="flex flex-col items-end gap-1 pr-2 pb-6 pl-6">
              <div className="flex w-full flex-col items-end gap-1 pr-2 pb-6 pl-6">
                {PRESETS.map((preset) => (
                  <PresetButton
                    key={preset.name}
                    preset={preset.name}
                    label={t ? t(`presets.${preset.name}`) : preset.label}
                    isSelected={selectedPreset === preset.name}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          {error?.isError && (
            <Alert
              variant="destructive"
              className={cn('flex items-center gap-4', isSmallScreen && 'max-w-[248px] p-2')}
            >
              <div className="h-4 w-4">
                <ExclamationTriangleIcon className="h-4 w-4" />
              </div>
              <AlertDescription>{error?.message}</AlertDescription>
            </Alert>
          )}
          <div className="flex w-full justify-between">
            {!isSmallScreen && (
              <div className="flex flex-col items-end gap-1 pr-2">
                <div className="flex w-full items-end gap-1 pr-2">
                  {GRANULARITY.map((preset) => (
                    <PresetGranularityButton
                      key={preset.name}
                      preset={preset.name}
                      label={t ? t(`granularity.${preset.name}`) : preset.label}
                      isSelected={selectedGranularityPreset === preset.name}
                    />
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 py-2 pr-4">
              <Button
                onClick={() => {
                  setIsOpen(false);
                  resetValues();
                }}
                variant="ghost"
              >
                {t ? t('buttons.cancel') : 'Cancel'}
              </Button>
              <Button
                onClick={() => {
                  const { days } = calculateDateDifference(range);
                  const constraint = GRANULARITY_CONSTRAINTS[selectedGranularityPreset];

                  if (constraint && constraint?.maxDays && days > constraint.maxDays) {
                    setError({
                      isError: true,
                      message: constraint.errorMessage,
                    });

                    return;
                  }

                  setError({
                    isError: false,
                    message: undefined,
                  });

                  setIsOpen(false);
                  onUpdate?.({
                    range,
                    rangeGranularity: selectedGranularityPreset as RangeGranularity,
                  });
                }}
              >
                {t ? t('buttons.update') : 'Update'}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

DateRangePicker.displayName = 'DateRangePicker';
